import { ILeaf, INode } from 'shared/types/tree'
import * as React from 'react'
import { BodyView } from './BodyView'
import { Box, Select, MenuItem, SelectChangeEvent, Button } from '@mui/material'
import { ActionMenu } from '../ActionMenu'
import { LeafTypes } from 'shared/types/ingestion'
import { useLeafOperations } from 'shared/api/tree'
import { getGradeId, getSubjectId } from 'shared/utils/tree'
import {
  nodeAttributionLeaves,
  nodeGradeLeaf,
  nodeSubjectLeaf,
} from '../utility/TreeHelpers'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { gradesByIdAtom } from 'shared/api/grades'
import { byStringFieldSort } from '../Helpers'
import { useSubjectsById } from 'shared/api/subjects'
import { useAllPartnerNames } from 'shared/hooks/partners.hooks'

export function LeafView({
  leaf,
  leafActions,
  parentNode,
}: {
  leaf: ILeaf
  leafActions: { title: string; action: (lId: string) => void }[]
  parentNode: INode
}) {
  if (leaf.type === LeafTypes.Attribution) {
    return <AttributionLeaf leaf={leaf} parentNode={parentNode} />
  }
  const currentLeafActions = leafActions.map(({ title, action }) => {
    return {
      title,
      action: () => action(leaf.id),
    }
  })
  return (
    <Box style={{ border: 'solid 1px', padding: 12, paddingTop: 0 }}>
      <Box
        display="flex"
        alignItems="center"
        style={{ borderBottom: 'solid 1px', marginBottom: 5 }}
      >
        Type: {leaf.type}
        {currentLeafActions.length > 0 && (
          <ActionMenu actions={currentLeafActions} />
        )}
      </Box>
      <BodyView body={leaf.body} />
    </Box>
  )
}

function AttributionLeaf({
  leaf,
  parentNode,
}: {
  leaf?: ILeaf
  parentNode: INode
}) {
  const [editing, setEditing] = React.useState(false)

  const actions = [{ title: 'Edit', action: () => setEditing(!editing) }]

  return (
    <Box style={{ border: 'solid 1px', padding: 12, paddingTop: 0 }}>
      <Box
        display="flex"
        alignItems="center"
        style={{ borderBottom: 'solid 1px', marginBottom: 5 }}
      >
        Type: {LeafTypes.Attribution}
        <ActionMenu actions={actions} />
      </Box>
      {editing && <AttributionEdit parentNode={parentNode} />}
      {!editing && (
        <Box>
          <Box>{`value: ${leaf?.body.value}`}</Box>
        </Box>
      )}
    </Box>
  )
}

export function AttributionEdit({ parentNode }: { parentNode: INode }) {
  const leaves = nodeAttributionLeaves(parentNode)
  if (leaves.length) {
    return (
      <Box>
        {leaves.map((leaf) => (
          <AttributionLeafEdit
            key={leaf.id}
            leaf={leaf}
            canDelete={leaves.length > 1}
          />
        ))}
      </Box>
    )
  }
  return <AttributionLeafCreate parentNode={parentNode} />
}

function AttributionLeafCreate({ parentNode }: { parentNode: INode }) {
  const attributions = useAllPartnerNames() ?? []
  const { createLeaf } = useLeafOperations({
    treeId: parentNode.tree_id,
    versionId: parentNode.version,
    nodeId: parentNode.id,
  })

  if (!attributions?.length) return <Box>Loading...</Box>

  const onSelect = ({ target: { value } }: SelectChangeEvent) => {
    return createLeaf(LeafTypes.Attribution, { value })
  }

  return (
    <Box>
      Create Attribution
      <Select value={''} onChange={onSelect}>
        {attributions.map((attr) => (
          <MenuItem key={attr} value={attr}>
            {attr}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

function AttributionLeafEdit({
  leaf,
  canDelete,
}: {
  leaf: ILeaf
  canDelete?: boolean
}) {
  const attributions = useAllPartnerNames() ?? []
  const { updateLeaf, deleteLeaf } = useLeafOperations({
    treeId: leaf.tree_id,
    versionId: leaf.version,
    nodeId: leaf.parent_id!,
  })
  const onSelect = ({ target: { value } }: SelectChangeEvent) => {
    return updateLeaf(leaf.id, { value })
  }

  return (
    <Box>
      <Select value={leaf.body.value ?? ''} onChange={onSelect}>
        {attributions.map((attr) => (
          <MenuItem key={attr} value={attr}>
            {attr}
          </MenuItem>
        ))}
      </Select>
      {canDelete && <Button onClick={() => deleteLeaf(leaf)}>Delete</Button>}
    </Box>
  )
}

export function GradeEdit({ parentNode }: { parentNode: INode }) {
  const grades = useAtomValue(gradesByIdAtom)
  const sorted = Object.values(grades).sort(byStringFieldSort('ordinality'))
  const { createLeaf, updateLeaf } = useLeafOperations({
    treeId: parentNode.tree_id,
    versionId: parentNode.version,
    nodeId: parentNode.id,
  })

  const leaf = nodeGradeLeaf(parentNode)
  const currentVal = getGradeId(parentNode)

  const onSelect = ({ target: { value } }: SelectChangeEvent<number>) => {
    const leafBody = { gradeID: value }
    if (!leaf) {
      return createLeaf(LeafTypes.Grade, leafBody)
    }
    return updateLeaf(leaf.id, leafBody)
  }

  return (
    <Box>
      <Select value={currentVal ?? 0} onChange={onSelect}>
        {sorted.map(({ name, id }) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

export function SubjectEdit({ parentNode }: { parentNode: INode }) {
  const subjects = useSubjectsById() ?? {}
  const sorted = Object.values(subjects).sort(byStringFieldSort('subject_name'))
  const { createLeaf, updateLeaf } = useLeafOperations({
    treeId: parentNode.tree_id,
    versionId: parentNode.version,
    nodeId: parentNode.id,
  })

  const leaf = nodeSubjectLeaf(parentNode)
  const currentVal = getSubjectId(parentNode)

  const onSelect = ({ target: { value } }: SelectChangeEvent<number>) => {
    const leafBody = { subjectID: value }
    if (!leaf) {
      return createLeaf(LeafTypes.Grade, leafBody)
    }
    return updateLeaf(leaf.id, leafBody)
  }

  return (
    <Box>
      <Select value={currentVal ?? 0} onChange={onSelect}>
        {sorted.map(({ subject_name, subject_id }) => (
          <MenuItem key={subject_id} value={subject_id}>
            {subject_name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}
